import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { first, get } from 'global/lib/lodash';
import { useUser } from 'global/hooks/useUser';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useHashView } from 'feature/share/useHashView';
import { SETTINGS } from 'feature/admin/account-settings';
import { getViewStyle } from 'redux/actions/view';
import { _size } from 'global/utils';

export const DEFAULT_COPYRIGHT_HOLDER = 'IBACOS';

/**
 * Resolve copyright company + optional realm id for that builder's logo.
 *
 * - Same-company (`companyId` matches the user's home builder realm): that company
 *   name + logo (never the realm-picker / site-context theme)
 * - Otherwise look up `companyId` in client-vendors (as clientId or vendorId)
 *   and use that company's name + logo
 * - IBACOS only when neither the user's company nor the owner company is found
 *
 * @returns {{ name: string, logoRealmId: string|null }}
 */
export function resolveCopyrightHolder({
  companyId,
  userRealmId,
  userRealmName,
  clientVendors = [],
} = {}) {
  const fallback = { name: DEFAULT_COPYRIGHT_HOLDER, logoRealmId: null };

  if (companyId == null || companyId === '') {
    return fallback;
  }

  const id = `${companyId}`;

  // Own-builder content: credit the user's home company (me.realm), not site context.
  if (userRealmId != null && id === `${userRealmId}`) {
    return { name: userRealmName || fallback.name, logoRealmId: id };
  }

  for (const cv of clientVendors || []) {
    // Prefer client match: shared editions are usually owned by the client builder
    // (e.g. Pulte owner_realm_id=2 on a row where vendor=IBACOS, client=Pulte).
    if (`${get(cv, 'clientId')}` === id || `${get(cv, 'client.id')}` === id) {
      const name = get(cv, 'client.name');
      return name ? { name, logoRealmId: id } : fallback;
    }
    if (`${get(cv, 'vendorId')}` === id || `${get(cv, 'vendor.id')}` === id) {
      const name = get(cv, 'vendor.name');
      return name ? { name, logoRealmId: id } : fallback;
    }
  }

  return fallback;
}

export function resolveCopyrightHolderName(opts) {
  return resolveCopyrightHolder(opts).name;
}

export function getCopyrightText(holderName = DEFAULT_COPYRIGHT_HOLDER) {
  return (
    <>
      © {holderName || DEFAULT_COPYRIGHT_HOLDER} {new Date().getFullYear()}. Created with PERFORM<sup>®</sup>
    </>
  );
}

function useClientVendorsForCopyright({ skip = false } = {}) {
  // Hashed share sessions cannot read /client-vendors (not on /sharehash/v1).
  // Skip the request entirely — callers use useHashOwnerBranding instead.
  return useRetrieveData(
    { url: '/client-vendors' },
    {
      build: ['clientVendors'],
      useCache: { reload: 20 },
      params: { include: 'vendor,client' },
      waitFor: skip ? null : undefined,
      showAlert: false,
    }
  );
}

function useCopyrightCompanyId({ ownerRealmId, vendorId } = {}) {
  // Home builder (me.realm) — not siteContextSelectedRealm / realm-picker.
  const { userRealm } = useUser();
  const companyId = vendorId != null && vendorId !== '' ? vendorId : ownerRealmId;
  const userRealmId = get(userRealm, 'id');
  const userRealmName = get(userRealm, 'name');
  return { companyId, userRealmId, userRealmName };
}

/**
 * Hashed Library view: resolve owner branding without client-vendors.
 * Loads realm name via /api/v1/realms/:id?selfRegister=1 and account-settings
 * by owner-realm-id (logo + theme). Falls back to IBACOS when unavailable.
 */
function useHashOwnerBranding(realmId) {
  const dispatch = useDispatch();
  const rid = realmId != null && realmId !== '' ? `${realmId}` : null;

  const { data: realm } = useRetrieveData(
    { url: `/realms/${rid || '0'}` },
    {
      build: ['realms'],
      params: { selfRegister: 1 },
      usePublicApi: true,
      useCache: { reload: 20 },
      useEffect: true,
      waitFor: !!rid,
      showAlert: false,
      overrideNotFound: true,
      transformData: (d) => first(d),
    }
  );

  const { data: settings } = useRetrieveData(
    { url: `/account-settings?realm_id=${rid || ''}` },
    {
      build: ['accountSettings'],
      usePublicApi: true,
      useCache: { reload: 20 },
      useEffect: true,
      waitFor: !!rid,
      showAlert: false,
      overrideNotFound: true,
    }
  );

  useEffect(() => {
    if (rid && _size(settings)) {
      dispatch(getViewStyle(rid, settings));
    }
  }, [rid, settings, dispatch]);

  const logo = useMemo(() => {
    if (!rid || !_size(settings)) return null;
    const logoSetting = [...settings].reverse().find((s) => get(s, 'name') === SETTINGS.logo);
    return get(logoSetting, 'value') || null;
  }, [rid, settings]);

  return {
    name: get(realm, 'name') || DEFAULT_COPYRIGHT_HOLDER,
    logo,
  };
}

/**
 * Hook: resolve copyright holder from edition ownerRealmId and/or assessment/task vendorId.
 * Prefer vendorId when both are provided (assessment/task context).
 *
 * Normal sessions fetch `/client-vendors` (not `useVendors`, which drops rows where
 * vendorId matches the current user's realm). Hashed share sessions skip that and
 * resolve the owner realm name via the public self-register realms endpoint.
 */
export function useCopyrightHolder({ ownerRealmId, vendorId } = {}) {
  const { active: hashViewActive } = useHashView();
  const { companyId, userRealmId, userRealmName } = useCopyrightCompanyId({
    ownerRealmId,
    vendorId,
  });
  const { data: clientVendors } = useClientVendorsForCopyright({ skip: hashViewActive });
  const hashBranding = useHashOwnerBranding(hashViewActive ? ownerRealmId || companyId : null);

  return useMemo(() => {
    if (hashViewActive) {
      return hashBranding.name;
    }
    return resolveCopyrightHolderName({
      companyId,
      userRealmId,
      userRealmName,
      clientVendors,
    });
  }, [hashViewActive, hashBranding.name, companyId, userRealmId, userRealmName, clientVendors]);
}

/**
 * Logo URL for the copyright owner builder.
 * Always loads Logo account-settings for the resolved copyright owner realm —
 * never `view.style` / realm-picker site context.
 * Hashed Library views load account-settings for owner-realm-id on /api/v1.
 */
export function useCopyrightBuilderLogo({ ownerRealmId, vendorId } = {}) {
  const { active: hashViewActive } = useHashView();
  const { companyId, userRealmId, userRealmName } = useCopyrightCompanyId({
    ownerRealmId,
    vendorId,
  });
  const { data: clientVendors } = useClientVendorsForCopyright({ skip: hashViewActive });
  const hashBranding = useHashOwnerBranding(hashViewActive ? ownerRealmId || companyId : null);

  const { logoRealmId } = useMemo(
    () =>
      hashViewActive
        ? { logoRealmId: null }
        : resolveCopyrightHolder({
            companyId,
            userRealmId,
            userRealmName,
            clientVendors,
          }),
    [hashViewActive, companyId, userRealmId, userRealmName, clientVendors]
  );

  const { data: settings } = useRetrieveData(
    { url: `/account-settings?realm_id=${logoRealmId}` },
    {
      build: ['accountSettings'],
      useCache: { reload: 20 },
      useEffect: true,
      waitFor: !hashViewActive && !!logoRealmId,
      showAlert: false,
    }
  );

  const ownerLogo = useMemo(() => {
    if (!logoRealmId || !settings) return null;
    const logoSetting = [...settings].reverse().find((s) => get(s, 'name') === SETTINGS.logo);
    return get(logoSetting, 'value') || null;
  }, [logoRealmId, settings]);

  if (hashViewActive) {
    return hashBranding.logo || null;
  }

  return ownerLogo;
}
